import {
  Component, Input, ChangeDetectionStrategy, ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
  BasicVsciModule,
  GenericValue,
  LogManager,
  ModelTransformerService,
  PersistenceService
} from '@visana-web/basic-ui';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NgForOf, NgIf} from "@angular/common";


export enum KEY_CODE {
  ESC = 27
}


@Component({
  selector: 'app-search-result-entry',
  templateUrl: './search-result-entry.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./search-result-entry.component.scss'],
  standalone: true,
  imports: [
    BasicVsciModule,
    TranslateModule,
    NgIf,
    NgForOf
  ]
})
export class SearchResultEntryComponent {
  protected center: google.maps.LatLng | undefined;

  @Input()
  set arzt(value: GenericValue | undefined) {
    this._arzt = value as GenericValue;
  }

  get arzt(): GenericValue | undefined{
    return this._arzt;
  }

  @Input()
  searchCriteria?: GenericValue;

  // ch.visana.restful.imprest.entities.RevealedGatekeeper
  private _arzt!: GenericValue;


  @Input() index?: number;

  latitude: number = 46.93813; // see basis_jsf2:BaseAppConstants
  longitude: number = 7.47727; // see basis_jsf2:BaseAppConstants
  zoom: number = 13; // base zoom level (default 8)

  public loadingDetails = false;

  // ch.visana.restful.imprest.entities.RevealedImprestAccount
  imprestAccount!: GenericValue;
  // ch.visana.restful.imprest.entities.RevealedImprestAccountPartner
  gatekeeperDetails!: GenericValue;


  constructor(
    private ref: ChangeDetectorRef,
    private elementRef: ElementRef,
    private modalService: NgbModal,
    private modelTransformerService: ModelTransformerService,
    private persistenceService: PersistenceService,
    private translateService: TranslateService,
    private logManager: LogManager) {
  }


  togglePopover(popover: { isOpen: () => any; close: () => void; open: () => void; }, event: MouseEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (popover.isOpen()) {
      popover.close();
    } else {
      popover.open();
    }
  }

  openUrl(url: string, event: MouseEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.open(url, '_blank');
  }

  open(content: any, event: MouseEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.modalService.open(content, {size: 'lg'}).result.then((result) => {
    }, (reason) => {
    });
  }


  loadDetails(event: MouseEvent) {
    event.preventDefault();
    this.isCollapsed = !this.isCollapsed;
    if (this.gatekeeperDetails) {
      this.ref.detectChanges();
      return;
    }
    this.loadingDetails = true;
    this.ref.detectChanges();
    const correctedUrl = this.gatekeeperDetailsUrl.replace('/imprestAccounts', '/imprestAccountsWeb');
    let req = correctedUrl;
    if (correctedUrl.endsWith('imprestAccountsWeb/')) {
      req = correctedUrl + this.gatekeeperNumber;
    }
    this.persistenceService.getGenericValue(req).subscribe(
      val => {
        this.imprestAccount = val;
        this.gatekeeperDetails = this.modelTransformerService.getModelGenericValue(this.imprestAccount, 'partner');
        // RevealedImprestAccountLocation
        const location = this.modelTransformerService.getModelGenericValue(this.imprestAccount, 'location');
        if (location) {
          this.latitude = this.modelTransformerService.getModelValueFloat(this.imprestAccount, 'location/latitude');
          this.longitude = this.modelTransformerService.getModelValueFloat(this.imprestAccount, 'location/longitude');
        } else {
          this.latitude = 0;
          this.longitude = 0;
        }
        this.center = new google.maps.LatLng(this.latitude, this.longitude);
        this.loadingDetails = false;
        this.ref.detectChanges();
      },
      error => {
        // TODO error handling
        this.loadingDetails = false;
        this.ref.detectChanges();
      },
    );
  }

  /**
   * Start mapping ch.visana.restful.imprest.entities.RevealedGatekeeper
   */
  get isCollapsed(): boolean {
    return this.modelTransformerService.getModelValueBoolean(this._arzt, 'collapsed');
  }

  set isCollapsed(value: boolean) {
    if (this.modelTransformerService.getModelValueBoolean(this._arzt, 'collapsed') === value) {
      return;
    }
    this.modelTransformerService.setModelValueBoolean(this._arzt, 'collapsed', value);
  }

  get isEmployed(): boolean {
    return this.modelTransformerService.getModelValueBoolean(this._arzt, 'gatekeeperEmployed');
  }

  get gatekeeperDetailsUrl(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperDetailsUrl');
  }

  // gets pdf url with current userLang path
  get gatekeeperDetailPdfUrl(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperDetailPdfUrl');
  }

  get gatekeeperNumber(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperNumber/value');
  }

  get displayName(): string {
    const val = this.modelTransformerService.getModelValue(this._arzt, 'displayName');
    if (this.searchCriteria) {
      const name = this.modelTransformerService.getModelValue(this.searchCriteria, 'name');
      if (name) {
        const startIndex = val.toLocaleLowerCase().indexOf(name.toLocaleLowerCase());
        if (startIndex > -1) {
          const marked = val.slice(0, startIndex) +
            '<mark>' +
            val.slice(startIndex, startIndex + name.length) +
            '</mark>' +
            val.slice(startIndex + name.length);
          return marked;
        }
      }
    }
    return this.modelTransformerService.getModelValue(this._arzt, 'displayName');
  }

  get displayLocality(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'displayLocality');
  }

  get gatekeeperAddress(): string {
    const isEmployed = this.modelTransformerService.getModelValueBoolean(this._arzt, 'gatekeeperEmployed');
    if (isEmployed) {
      const street = this.modelTransformerService.getModelValue(this._arzt, 'employerStreet');
      const streetNr = this.modelTransformerService.getModelValue(this._arzt, 'employerStreetNr');
      const streetNr2 = streetNr ? streetNr : '';
      if (street) {
        return street + ' ' + streetNr2 + '<br/>';
      }
      return '';
    }
    const street = this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperStreet');
    const streetNr = this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperStreetNr');
    const streetNr2 = streetNr ? streetNr : '';
    if (street) {
      return street + ' ' + streetNr2 + '<br/>';
    }
    return '';
  }

  get postalCode(): string {
    const postalCode = this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperPostCode/postalCode');
    this.logManager.info('postalCode=', postalCode);
    return postalCode;
  }

  get gatekeeperLocality(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'gatekeeperLocality');
  }

  get employerPostCode(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'employerPostCode/postalCode');
  }

  get employerLocality(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'employerLocality');
  }

  get billerTypeDescription(): string {
    return this.modelTransformerService.getModelValue(this._arzt, 'billerTypeDescription');
  }

  get networkAffiliationsInsuranceModels(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this._arzt, 'networkAffiliations', 'insuranceModel');
  }

  get networkAffiliationsMedicalNetworks(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this._arzt, 'networkAffiliations', 'medicalNetworkName');
  }

  get networkAffiliationsGatekeeperRole(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this._arzt, 'networkAffiliations', 'gatekeeperRole');
  }

  get networkAffiliationsPatientAdmissionFreeze(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this._arzt, 'networkAffiliations', 'patientAdmissionFreeze');
  }

  /**
   * End mapping ch.visana.restful.imprest.entities.RevealedGatekeeper
   */

  /**
   * Start mapping ch.visana.restful.imprest.entities.RevealedImprestAccount
   */
  get employeesDescription(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this.imprestAccount, 'employees', 'description');
  }

  get employersDescription(): string[] {
    return this.modelTransformerService.getModelCollectionValues(this.imprestAccount, 'employers', 'description');
  }

  /**
   * End mapping ch.visana.restful.imprest.entities.RevealedImprestAccount
   */

  /**
   * Start mapping ch.visana.restful.imprest.entities.RevealedImprestAccountPartner
   */
  get surgeryName(): string {
    return this.modelTransformerService.getModelValue(this.gatekeeperDetails, 'surgeryName');
  }

  get partnerNumber(): string {
    return this.modelTransformerService.getModelValue(this.gatekeeperDetails, 'partnerNumber/value');
  }

  get postalAddressStreet(): string {
    return this.modelTransformerService.getModelCollectionValueByIndex(this.gatekeeperDetails, 'contactData', 0, 'postalAddress/street');
  }

  get postalAddressBuildingNumber(): string {
    return this.modelTransformerService.getModelCollectionValueByIndex(this.gatekeeperDetails, 'contactData', 0, 'postalAddress/buildingNumber');
  }

  get postalAddressPostalCode(): string {
    return this.modelTransformerService.getModelCollectionValueByIndex(this.gatekeeperDetails, 'contactData', 0, 'postalAddress/postalCode/postalCode');
  }

  get postalAddressLocality(): string {
    return this.modelTransformerService.getModelCollectionValueByIndex(this.gatekeeperDetails, 'contactData', 0, 'postalAddress/locality');
  }

  get phoneContacts(): string[][] {
    return this.modelTransformerService.getModelCollectionValuesPair(this.gatekeeperDetails, 'contactData', 'channel', 'phoneNumber/rawValue');
  }

  get eMailContacts(): string[][] {
    return this.modelTransformerService.getModelCollectionValuesPair(this.gatekeeperDetails, 'contactData', 'channel', 'mailAddress/address');
  }

  get internetContacts(): string[][] {
    const hosts = this.modelTransformerService.getModelCollectionValuesPair(this.gatekeeperDetails, 'contactData', 'channel', 'url/host');
    const pathes = this.modelTransformerService.getModelCollectionValuesPair(this.gatekeeperDetails, 'contactData', 'channel', 'url/path');
    const contacts = hosts;
    let idx = 0;
    for (let path of pathes) {
      if (path) {
        contacts[idx][1] = contacts[idx][1] + path[1];
      }
      idx++;
    }
    this.logManager.debug('internetContacts=', contacts);
    return contacts;
  }

  get name(): string {
    return this.modelTransformerService.getModelValue(this.gatekeeperDetails, 'name');
  }

  get givenName(): string {
    return this.modelTransformerService.getModelValue(this.gatekeeperDetails, 'givenName');
  }

  /**
   * End mapping ch.visana.restful.imprest.entities.RevealedImprestAccountPartner
   */

  public ensureUrl(value: string): string {
    if (!value) {
      return value;
    }
    if (value.startsWith('http')) {
      return value;
    }
    return 'http://' + value;
  }

  public ensureDisplayUrl(value: string): string {
    if (!value) {
      return value;
    }
    value = value.replace('http://', '');
    //value = value.replace('www.', '');
    return value;
  }

  get markerPosition(): google.maps.LatLng {
    return new google.maps.LatLng(this.latitude, this.longitude);
  }

  markerOptions: google.maps.MarkerOptions = {
    animation: google.maps.Animation.DROP,
  };

  handleAffe(affi: string) {
    if (affi === 'InsuranceModel.HAM')
      return 'InsuranceModel.MANAGED_CARE.SHORT'

    return affi + ".SHORT"
  }

  generateResultName() {
    return this.name + ' '
      +this.givenName +' '
      +this?.surgeryName +' '
      +this?.postalAddressLocality +' '
      +this.postalAddressStreet +' '
      +this.postalAddressBuildingNumber +' '
      +this.postalAddressPostalCode +' '
      +this.postalAddressLocality;
  }
}
